import React from 'react';
import { Target, Eye, Award, Users, Shield, Globe, Zap, CheckCircle } from 'lucide-react';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <Award size={40} />,
      title: 'Integrity & Transparency',
      description: 'We operate with honesty and openness in all our engagements'
    },
    {
      icon: <Users size={40} />,
      title: 'Customer First',
      description: 'Your success is our priority, always'
    },
    {
      icon: <Target size={40} />,
      title: 'Technical Excellence',
      description: 'Delivering world-class technical expertise'
    },
    {
      icon: <Eye size={40} />,
      title: 'Continuous Innovation',
      description: 'Staying ahead with cutting-edge solutions'
    }
  ];

  const unsplashAboutImages = [
    'https://images.unsplash.com/photo-1550745665-5bcfa8209023?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1558494949-ef010cbffb31?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1580678683292-cd3ec928cde0?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
  ];

  const capabilities = [
    {
      icon: <Shield size={48} />,
      title: 'Security Expertise',
      description: 'Deep knowledge in cybersecurity operations and threat management',
      features: ['SOC Operations', 'Threat Intelligence', 'Compliance Management']
    },
    {
      icon: <Globe size={48} />,
      title: 'Global Operations',
      description: 'Worldwide infrastructure management with 24x7 support',
      features: ['Multi-Region Coverage', 'Follow-the-Sun Model', 'Global Teams']
    },
    {
      icon: <Zap size={48} />,
      title: 'High Performance',
      description: 'Optimized systems delivering maximum efficiency',
      features: ['99.9% Uptime', 'Fast Response Times', 'Scalable Architecture']
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-background">
          <img 
            src={unsplashAboutImages[0]} 
            alt="About Hero Background" 
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">About Kevitco Technologies</div>
            <h1 className="page-title">Building the Future of IT Operations</h1>
            <p className="page-subtitle">
              Experience, expertise, and excellence in enterprise technology solutions
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <div className="section-header">
                <h2 className="section-title">Who We Are</h2>
                <p className="section-subtitle">
                  Trusted technology partner for global enterprises
                </p>
              </div>
              <div className="about-description">
                <p>
                  Kevitco Technologies was founded by experienced IT professionals with deep 
                  expertise in Security Operations, Network Operations, Data Center Management, 
                  and Enterprise Product Support.
                </p>
                <p>
                  Our team has worked with global organizations managing mission-critical 
                  environments and 24x7 operations. We bring industry best practices, 
                  operational excellence, and a customer-first mindset to every engagement.
                </p>
                <p>
                  With a proven track record of delivering reliable, secure, and scalable IT 
                  services, we have established ourselves as a trusted technology partner for 
                  enterprises worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section" style={{ background: '#fafafa' }}>
        <div className="container">
          <div className="mission-vision">
            <div className="mv-card">
              <div className="mv-icon">
                <Target size={50} />
              </div>
              <h3>Our Mission</h3>
              <p>
                Deliver reliable, secure, and scalable IT services that empower organizations 
                to focus on their core business while we manage their technology infrastructure 
                with excellence.
              </p>
            </div>
            <div className="mv-card">
              <div className="mv-icon">
                <Eye size={50} />
              </div>
              <h3>Our Vision</h3>
              <p>
                Become a trusted global technology partner recognized for operational excellence, 
                innovation, and unwavering commitment to customer success in IT operations and 
                security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section" style={{ background: '#fafafa' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Capabilities</h2>
            <p className="section-subtitle">
              Comprehensive expertise across enterprise IT domains
            </p>
          </div>
          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-card">
                <div className="capability-icon">{capability.icon}</div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
                <ul className="capability-features">
                  {capability.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Kevitco?</h2>
            <p className="section-subtitle">
              The advantages of partnering with us
            </p>
          </div>
          <div className="why-grid">
            <div className="why-item">
              <h4>Global Experience</h4>
              <p>Team with extensive experience managing enterprise IT operations worldwide</p>
            </div>
            <div className="why-item">
              <h4>24x7 Operations</h4>
              <p>Round-the-clock support with follow-the-sun delivery model</p>
            </div>
            <div className="why-item">
              <h4>Industry Best Practices</h4>
              <p>Proven methodologies and frameworks for operational excellence</p>
            </div>
            <div className="why-item">
              <h4>Customer-Centric</h4>
              <p>Tailored solutions designed around your unique business needs</p>
            </div>
            <div className="why-item">
              <h4>Security First</h4>
              <p>Comprehensive security measures to protect your critical assets</p>
            </div>
            <div className="why-item">
              <h4>Scalable Solutions</h4>
              <p>Infrastructure and services that grow with your business</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
